import {
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
  Key,
} from 'react';
// 组件
import { Table } from 'antd';
import { ProTable, ProTooltip, } from '../antd';
import EditableWrapper from './subpage/editableWrapper';
import TotalTable, { TotalTableActionType } from './subpage/totalTable';
import EditableRow from './subpage/editableRow';
import EditableCell from './subpage/editableCell';
import { PlusOutlined, FullscreenOutlined, } from '@ant-design/icons';
// 方法
import classNames from 'classnames';
import EdiTableContext from './context';
import useEdiTableContext from './hooks/useEdiTableContext';
import useEdiTableColumnsHook from './hooks/ediTableColumnsHook';
import { useLayoutContext } from '../PageLayout/context/LayoutContext';
import { getRecordCode, formatDataSource, filterDataSource } from './utils';
import { exitFullscreen, filterEditObjEmptyValue, requestFullScreen } from '../utils/utils';
// 常量
import { FormInstance } from 'antd/lib/form';
import {
  STATUS_CREATED,
  STATUS_CREATED_CODE,
  HISTORY_LIST_DATA,
} from '../constants/constant';
import { EdiTableProps } from './interface';
import './style.less';

/** 可编辑表 即将移除请使用EdiTable */
const EdiTable: FC<EdiTableProps> = props => {
  const {
    actionRef,
    maxHeight = 300,
    sort,
    sortFieldName,
    moveSort,
    className,
    style,
    bordered,
    mergeRowKey,
    totalKeys,
    totalIndex,
    totalText,
    showFullIcon,
    onValuesChange,
    rowRender,
  } = props;
  const [expandedKeys, setExpandedKeys] = useState<Key[]>([]);
  const [tableMaxHeight, setTableMaxHeight] = useState<number>(maxHeight);
  const tableRef = useRef<HTMLDivElement>(null);
  const forms = useRef<FormInstance[]>([]);
  const flagIndex = useRef<number>(0);
  const totalTableRef = useRef<TotalTableActionType>();
  const isSort = sort || moveSort !== false;
  const { enumData } = useLayoutContext();
  const { dataSource, dispatchEdiTable } = useEdiTableContext(
    flagIndex,
    props.dataSource,
    forms,
    !!isSort,
  );
  useEffect(() => {
    setTableMaxHeight(maxHeight);
    if (!showFullIcon) return;
    const listener = () => {
      if (document.fullscreenElement) {
        /** 全屏 */
        const tableHeader= tableRef.current?.getElementsByClassName('ant-table-header')?.[0];
        const bodyHeight = document.body.clientHeight;
        const screenHeight = window.screen.height;
        const tableHeaderTop = tableHeader ? tableHeader.clientHeight : 37;
        const totalHeight = totalKeys ? 35 : 0;
        const pageHeight = screenHeight > bodyHeight ? screenHeight : bodyHeight;
        setTableMaxHeight(pageHeight - tableHeaderTop - totalHeight);
      } else {
        /** 退出全屏 */
        setTableMaxHeight(maxHeight);
      }
    };
    document.addEventListener('fullscreenchange', listener);
    return () => {
      document.removeEventListener('fullscreenchange', listener);
    };
  }, [maxHeight]);
  /** 同步dataSource */
  const handleUpdateDataSource = () => {
    if (Array.isArray(totalKeys) && totalKeys.length) {
      dispatchEdiTable('changeDataSource', {
        updateDataSource: handleDataSource(),
      });
    }
  };
  const { columns } = useEdiTableColumnsHook(
    props,
    dispatchEdiTable,
    handleUpdateDataSource,
    tableRef.current,
    enumData,
  );
  useEffect(() => {
    if (mergeRowKey)
      setExpandedKeys((dataSource || []).map(v => v[mergeRowKey]));
  }, [dataSource]);
  /** 全屏 */
  const handleFull = () => {
    document.fullscreenElement ? exitFullscreen() : requestFullScreen(tableRef.current);
  };
  /** 增加行 */
  const handleAdd = useCallback(
    (addItem?: any, isEmpty?: boolean) => {
      handleUpdateDataSource();
      dispatchEdiTable('addRow', { addItem, isEmpty });
    },
    [dispatchEdiTable],
  );
  // 提交
  const handleSubmit = async () => {
    if (!dataSource) return [];
    let flag = true;
    const newDataSource: any[] = [];
    const { effectiveData, deleteData } = formatDataSource(dataSource);
    let newItem: any = {};
    for (let i = 0, len = effectiveData.length; i < len; i++) {
      try {
        const item = effectiveData[i];
        const itemForm = forms.current?.[item.key];
        if (itemForm) {
          const itemFormData = await itemForm.validateFields();
          newItem = filterEditObjEmptyValue({
            ...item,
            ...itemFormData,
            [STATUS_CREATED_CODE]: getRecordCode(
              item[STATUS_CREATED_CODE],
              itemFormData[STATUS_CREATED_CODE],
            ),
          });
          if (isSort && sortFieldName) newItem[sortFieldName] = i + 1;
        } else {
          newItem = item;
        }
        delete newItem.key;
        delete newItem[HISTORY_LIST_DATA];
        delete newItem.sortIndex;
        newDataSource.push(newItem);
      } catch (errorInfo) {
        flag = false;
      }
    }
    return flag ? [...deleteData, ...newDataSource] : false;
  };
  // 取值
  const handleDataSource = (filterDeleteData?: boolean) => {
    const currentDataSource: any[] = [];
    if (!dataSource) return currentDataSource;
    const { effectiveData, deleteData } = formatDataSource(dataSource);
    effectiveData.forEach((item, k) => {
      const itemForm = forms.current?.[item.key];
      let newItem: any = item;
      if (itemForm) {
        const itemFormData = itemForm.getFieldsValue(true);
        newItem = {
          ...item,
          ...itemFormData,
          [STATUS_CREATED_CODE]: getRecordCode(
            item[STATUS_CREATED_CODE],
            itemFormData[STATUS_CREATED_CODE],
          ),
        };
      }
      if (isSort && sortFieldName) newItem[sortFieldName] = k + 1;
      currentDataSource.push(newItem);
    });
    return filterDeleteData ? currentDataSource : [...deleteData, ...currentDataSource];
  };
  // 删除
  const handleDelete = (record: any) => {
    const recordKey = record?.key;
    handleUpdateDataSource();
    dispatchEdiTable('deleteRow', { index: recordKey });
  };
  // 清空
  const handleEmpty = () => {
    const newDataSource = dataSource?.filter(
      item => item[STATUS_CREATED_CODE] !== STATUS_CREATED.ADD,
    );
    dispatchEdiTable('changeDataSource', {
      updateDataSource: newDataSource?.map(item => ({
        ...item,
        [STATUS_CREATED_CODE]: STATUS_CREATED.DELETE,
      })),
    });
    forms.current.forEach(formItem => formItem?.resetFields?.());
  };
  /** 表单字段值改变 */
  const handleValuesChange = (
    value: any,
    changedValues: any,
    rowValues: any,
    index: number,
    form: FormInstance,
  ) => {
    if (Array.isArray(totalKeys) && totalKeys.length) {
      const changeName = Object.keys(changedValues)[0];
      const findItem = totalKeys.find(v => v.dataIndex === changeName);
      if (findItem) {
        const effectiveDataSource = handleDataSource().filter(
          v => v[STATUS_CREATED_CODE] !== STATUS_CREATED.DELETE,
        );
        totalTableRef.current?.onChange(effectiveDataSource);
      }
    }
    onValuesChange?.(value, changedValues, rowValues, index, form);
  };
  /** 更新统计数据 */
  const handleChangeTotal = (tableData?: any[]) => {
    if (tableData) {
      totalTableRef.current?.onChange(tableData);
    } else {
      const effectiveDataSource = handleDataSource().filter(
        v => v[STATUS_CREATED_CODE] !== STATUS_CREATED.DELETE,
      );
      totalTableRef.current?.onChange(effectiveDataSource);
    }
  };
  const currentDataSource = useMemo(() => {
    return filterDataSource(dataSource, mergeRowKey);
  }, [dataSource, mergeRowKey]);
  const classes = classNames('yb-editable', className);
  if (actionRef) {
    actionRef.current = {
      add: handleAdd,
      getDataSource: handleDataSource,
      delete: handleDelete,
      submit: handleSubmit,
      empty: handleEmpty,
      changeTotal: handleChangeTotal,
      forms: forms.current,
      tableElement: tableRef.current,
    };
  }
  return (
    <EdiTableContext.Provider
      value={{
        dataSource,
        dispatchEdiTable,
        forms,
        editableRef: tableRef.current,
      }}
    >
      <>
        <div
          className={classes}
          style={style}
          ref={tableRef}
          onMouseLeave={() => {
            showFullIcon && tableRef.current?.classList.remove('yb-editable-active');
          }}
          onMouseEnter={() => {
            showFullIcon && tableRef.current?.classList.add('yb-editable-active');
          }}
        >
          {
            showFullIcon && (
              <div className='yb-editable-toolbar'>
                <div className='yb-editable-toolbar-wrapper'>
                  <span className='toolbar-icon' onClick={handleFull}>
                    <ProTooltip
                      placement="top"
                      title={document.fullscreenElement ? '退出全屏' : '全屏'}
                      getPopupContainer={() => tableRef.current || document.body}
                    >
                      <FullscreenOutlined />
                    </ProTooltip>
                  </span>
                </div>
              </div>
            )
          }
          <ProTable
            className="yb-editable"
            columns={columns}
            dataSource={currentDataSource}
            pagination={false}
            bordered={bordered}
            size="small"
            expandable={
              mergeRowKey
                ? {
                    defaultExpandAllRows: true,
                    expandedRowKeys: expandedKeys,
                    onExpand: (expanded, record) => {
                      let currentExpandedKeys: Key[] = expandedKeys;
                      if (expanded) {
                        currentExpandedKeys.push(record[mergeRowKey]);
                      } else {
                        currentExpandedKeys = currentExpandedKeys.filter(
                          v => v !== record[mergeRowKey],
                        );
                      }
                      setExpandedKeys(currentExpandedKeys);
                    },
                  }
                : undefined
            }
            components={{
              body: {
                wrapper: EditableWrapper,
                row: EditableRow,
                cell: EditableCell,
              },
            }}
            scroll={{
              scrollToFirstRowOnChange: true,
              y: tableMaxHeight,
            }}
            // @ts-ignore
            onRow={(record, index) => {
              return {
                rowRender,
                sortIndex: index,
                record,
                mergeRowKey,
                onValuesChange: handleValuesChange,
              };
            }}
            summary={() => {
              if (
                Array.isArray(totalKeys) &&
                !!totalKeys.length &&
                Array.isArray(currentDataSource) &&
                !!currentDataSource.length
              ) {
                return (
                  <Table.Summary fixed>
                    <TotalTable
                      actionRef={totalTableRef}
                      columns={columns}
                      totalKeys={totalKeys}
                      dataSource={currentDataSource}
                      totalIndex={totalIndex}
                      totalText={totalText}
                    />
                  </Table.Summary>
                );
              }
            }}
          />
        </div>
      </>
    </EdiTableContext.Provider>
  );
};

EdiTable.defaultProps = {
  disabled: false,
  hideAdd: false,
  addIcon: <PlusOutlined />,
  sort: false,
  showSortColumn: false,
  maxHeight: 300,
  sortFieldName: 'displayOrder',
  bordered: true,
  showFullIcon: false,
  operationWidth: 50,
};

export default EdiTable;
